// Hub principal du permis AM (BSR)
// Landing dédiée : /permis-am
// Structure réglementaire stricte : 8 h de formation pratique = 3 h hors circulation + 3 h circulation + 2 h sensibilisation risques
// Public : cyclomoteurs (scooter/mob 50 cm³) — dès 14 ans

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ArrowRight, Bike, ShieldAlert, Sparkles,
  BookOpen, Clock, Users, Home, FileCheck, GraduationCap, Car, Download,
} from 'lucide-react';
import useSeo from '../../hooks/useSeo';
import ProgrammeSlidesCyclomoteur from '../../components/permis-am/ProgrammeSlidesCyclomoteur';
import TeacherOnlyGate from '../../components/TeacherOnlyGate';

// Les 3 modules réglementaires du permis AM
const MODULES = [
  {
    id: 'hors-circulation',
    num: '01',
    duree: '3 h',
    kicker: 'MODULE 1 · HORS CIRCULATION',
    title: 'Prise en main du véhicule',
    subtitle: 'Découverte, équipement, maniabilité à basse allure',
    desc: 'Tu apprends à connaître ta machine : commandes, position, démarrage, arrêt, allure lente, virages, freinage progressif. Tout se passe sur une zone fermée avant de rouler en circulation.',
    icon: Bike,
    accent: '#cf2031',
    href: '/cours/permis-am-journee-1',
    sessions: 'Journée 1 · 14 chapitres disponibles',
  },
  {
    id: 'circulation',
    num: '02',
    duree: '3 h',
    kicker: 'MODULE 2 · CIRCULATION',
    title: 'Ta première conduite sur la route',
    subtitle: 'Analyser, anticiper, cohabiter avec les autres',
    desc: 'Tu conduis en situation réelle : intersections, priorités, allure adaptée, distances de sécurité, communication avec les autres usagers, et surtout : tu apprends à voir venir les dangers.',
    icon: FileCheck,
    accent: '#cf2031',
    href: '/cours/permis-am-journee-2',
    sessions: 'Journée 2 · 14 chapitres disponibles',
  },
  {
    id: 'sensibilisation',
    num: '03',
    duree: '2 h',
    kicker: 'MODULE 3 · SENSIBILISATION',
    title: 'Comprendre les risques',
    subtitle: 'En présence de tes parents (obligatoire si tu es mineur)',
    desc: "Un temps d'échange essentiel : vulnérabilité, vitesse, téléphone, alcool et substances, fatigue, pression du groupe, équipement, plan B. 15 séquences dont 5 scénarios interactifs « Que ferais-tu ? » et une checklist d'engagement personnel.",
    icon: ShieldAlert,
    accent: '#f59e0b',
    href: '/cours/permis-am-sensibilisation',
    sessions: 'Séquence dédiée · 15 chapitres',
  },
];

// Les catégories de cyclomoteurs autorisés (formation dédiée cyclomoteur — pas de VSP)
const CATEGORIES = [
  {
    id: 'scooter',
    icon: Bike,
    title: 'Scooter 50 cm³',
    subtitle: 'Thermique',
    detail: 'Vitesse max 45 km/h · dès 14 ans',
  },
  {
    id: 'mob',
    icon: Bike,
    title: 'Mob / Cyclo 50 cm³',
    subtitle: 'Thermique 2 temps ou 4 temps',
    detail: 'Vitesse max 45 km/h · dès 14 ans',
  },
  {
    id: 'cyclo-elec',
    icon: Bike,
    title: 'Cyclo électrique',
    subtitle: 'Équivalent 50 cm³',
    detail: 'Puissance ≤ 4 kW · vitesse max 45 km/h',
  },
];

// Conditions d'accès
const CONDITIONS = [
  { label: 'ÂGE MINIMUM', value: '14 ans révolus', icon: Users },
  { label: 'ASSR / AAER', value: 'ASSR de niveau 2 ou AAER obligatoire', icon: FileCheck },
  { label: 'FORMATION', value: '8 h minimum en école agréée', icon: Clock },
  { label: 'PARENTS', value: 'Présence obligatoire à la sensibilisation (mineur)', icon: Users },
];

export default function PermisAmHub() {
  useSeo({
    title: 'Permis AM (BSR) — Code 2 Roues · Formation cyclomoteur',
    description: "Découvre le permis AM (ex-BSR) : 8 h de formation obligatoire pour conduire un scooter ou un cyclomoteur 50 cm³ dès 14 ans. Modules hors circulation, circulation et sensibilisation aux risques.",
  });

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 print:pb-0" data-testid="permis-am-hub">
      {/* Barre du haut spécifique AM */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="ghost" size="sm" data-testid="btn-back-home">
            <Link to="/" reloadDocument><ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline"> Retour</span></Link>
          </Button>
          <div className="hidden md:block flex-1 text-center">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-500">Permis AM · BSR</div>
          </div>
          <div className="flex-1 md:hidden" />
          <Button asChild variant="outline" size="sm">
            <Link to="/" reloadDocument><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline"> Accueil</span></Link>
          </Button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {/* ============= HERO ============= */}
        <header className="text-center pt-6" data-testid="am-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 mb-5">
            <GraduationCap className="w-3 h-3" /> Formation obligatoire · Dès 14 ans
          </div>
          <h1
            className="text-3xl sm:text-5xl md:text-6xl font-black leading-tight mb-3 tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Permis AM
          </h1>
          <p className="text-lg md:text-2xl text-red-500 font-black tracking-wide mb-6">
            Ta première formation officielle à la conduite
          </p>

          <div className="max-w-2xl mx-auto p-4 md:p-5 rounded-xl border border-red-500/30 bg-red-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-2">
              Fil conducteur
            </div>
            <div
              className="text-sm md:text-base font-black text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              CONNAÎTRE → COMPRENDRE → ANTICIPER → RESPECTER → PROTÉGER
            </div>
          </div>

          <p className="mt-6 text-sm md:text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Le permis AM (anciennement <strong className="text-foreground">BSR</strong>) est
            <strong className="text-foreground"> obligatoire</strong> pour conduire un cyclomoteur (scooter, mob ou cyclo électrique 50 cm³)
            <strong> ou une voiturette (quadricycle léger)</strong> dès <strong className="text-foreground">14 ans</strong>.
            La formation dure <strong className="text-foreground">8 heures minimum</strong>.
            Deux options existent : cyclomoteur (structure 3 modules) et voiturette (5 séquences).
          </p>
        </header>

        {/* Programme visuel Cyclomoteur — réservé aux enseignants Code 2 Roues */}
        <TeacherOnlyGate
          title="Programme visuel Cyclomoteur · réservé aux moniteurs"
          description="Les 3 slides pédagogiques du programme cyclomoteur (Journée 1, Journée 2 et Sensibilisation) sont un support de cours. Elles sont accessibles aux enseignants Code 2 Roues connectés."
          accentColor="#0284c7"
          testId="programme-cyclo-gate"
        >
          <ProgrammeSlidesCyclomoteur />
        </TeacherOnlyGate>

        {/* ============= SÉLECTEUR OPTION AM ============= */}
        <section data-testid="am-option-selector" className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-3 text-center">
            Choisis ton option de formation AM
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* CYCLO — reste sur la même page */}
            <div
              className="relative rounded-2xl border-2 border-red-500/50 bg-gradient-to-br from-red-500/15 to-transparent p-5"
              data-testid="am-option-cyclo"
            >
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/30 border border-red-500/50 text-[9.5px] font-black uppercase tracking-widest text-red-100">
                Sélectionné
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-red-500/25 border border-red-500/40 text-red-300 flex items-center justify-center flex-shrink-0">
                  <Bike className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-red-400">Option 1 · Cyclomoteur</div>
                  <h3 className="text-lg font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Scooter · mob · cyclo électrique
                  </h3>
                  <p className="text-white/70 text-[12.5px] mt-1">
                    Formation en 3 modules — 8 h. Public : jeunes 14-16 ans qui veulent circuler en 2-roues motorisé.
                  </p>
                </div>
              </div>
              <div className="text-[11px] text-red-300/85 font-semibold">Tu es sur la bonne page ↓</div>
            </div>

            {/* VOITURETTE — lien vers /permis-am/voiturette */}
            <Link
              to="/permis-am/voiturette"
              className="relative rounded-2xl border-2 border-white/15 hover:border-red-500/50 bg-[#141416] transition p-5 block group"
              data-testid="am-option-voiturette"
            >
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/25 border border-emerald-500/40 text-[9.5px] font-black uppercase tracking-widest text-emerald-200">
                Nouveau
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-red-500/15 border border-red-500/25 text-red-300 flex items-center justify-center flex-shrink-0">
                  <Car className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-red-400">Option 2 · Quadricycle léger</div>
                  <h3 className="text-lg font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Voiturette · VSP
                  </h3>
                  <p className="text-white/70 text-[12.5px] mt-1">
                    Formation en <strong>5 séquences officielles</strong> — 8 h sur 2 jours. Assis, ceinturé, 45 km/h max.
                  </p>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-red-400 group-hover:gap-2.5 transition-all">
                Ouvrir la formation voiturette <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>
        </section>

        {/* ============= 2 POSTERS PREMIUM (Journées) ============= */}
        <section data-testid="am-posters">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-3 text-center">
            La formation en 2 journées
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/cours/permis-am-journee-1"
              className="group relative block overflow-hidden rounded-2xl border border-red-500/30 hover:border-red-500/70 hover:shadow-[0_0_40px_-10px_rgba(207,32,49,0.5)] transition-all"
              data-testid="am-poster-j1"
            >
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/gioeox72_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2017_32_59.png"
                alt="Poster Journée 1 · Plateau — Maîtriser les bases (Code 2 Roues)"
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400">Journée 1 · Plateau</div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-white group-hover:gap-2.5 transition-all">
                    Découvrir <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
            <Link
              to="/cours/permis-am-journee-2"
              className="group relative block overflow-hidden rounded-2xl border border-blue-500/30 hover:border-blue-500/70 hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)] transition-all"
              data-testid="am-poster-j2"
            >
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/5e82x34k_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2017_35_04.png"
                alt="Poster Journée 2 · Circulation — Voir plus loin, agir mieux (Code 2 Roues)"
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-400">Journée 2 · Circulation</div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-white group-hover:gap-2.5 transition-all">
                    Découvrir <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* ============= 3 TYPES DE CYCLOMOTEURS ============= */}
        <section data-testid="am-categories">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-3 text-center">
            3 types de cyclomoteurs autorisés
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.id}
                  className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-black/20"
                  data-testid={`am-category-${cat.id}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <div
                      className="text-lg font-black text-foreground leading-tight"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {cat.title}
                    </div>
                    <div className="text-sm text-red-400 font-bold">{cat.subtitle}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{cat.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============= BRIEFING CIRCULATION (raccourci prof) ============= */}
        <section data-testid="am-briefing-shortcut">
          <Link
            to="/briefing-circulation"
            reloadDocument
            className="block rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-600/20 via-red-500/8 to-transparent p-4 sm:p-5 hover:border-red-500/70 active:scale-[0.99] transition-all shadow-lg"
            data-testid="am-briefing-link"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center">
                <Bike className="w-6 h-6 sm:w-7 sm:h-7 text-red-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-1">
                  🛵 Sur le parking, avant de partir
                </div>
                <div
                  className="text-lg sm:text-xl font-black leading-tight text-foreground"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Mon Briefing Circulation
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  Sors ton téléphone, ouvre le QR, les élèves scannent depuis leur portable · 7 réflexes + QCM d&apos;ancrage
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-red-300 flex-shrink-0" />
            </div>
          </Link>
        </section>

        {/* ============= PROGRAMME OFFICIEL (raccourci pédagogique) ============= */}
        <section data-testid="am-programme-shortcut">
          <Link
            to="/permis-am/programme"
            reloadDocument
            className="block rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-600/20 via-emerald-500/8 to-transparent p-4 sm:p-5 hover:border-emerald-500/70 active:scale-[0.99] transition-all shadow-lg"
            data-testid="am-programme-link"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">
                  ✓ Référentiel officiel · 5 séquences
                </div>
                <div
                  className="text-lg sm:text-xl font-black leading-tight text-foreground"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Voir le programme officiel du permis AM
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  Tous les objectifs officiels (S1→S5) mappés vers nos chapitres · pour prof et élève
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-emerald-300 flex-shrink-0" />
            </div>
          </Link>
        </section>

        {/* ============= CÉRÉMONIE FIN DE FORMATION (raccourci final) ============= */}
        <section data-testid="am-ceremonie-shortcut">
          <Link
            to="/permis-am/ceremonie"
            reloadDocument
            className="block rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-600/25 via-amber-500/12 to-transparent p-4 sm:p-5 hover:border-yellow-500/80 active:scale-[0.99] transition-all shadow-lg relative overflow-hidden"
            data-testid="am-ceremonie-link"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-yellow-500/10 blur-2xl pointer-events-none" />
            <div className="flex items-center gap-3 sm:gap-4 relative">
              <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-yellow-500/25 border-2 border-yellow-500/60 flex items-center justify-center">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-yellow-400 mb-1 flex items-center gap-2 flex-wrap">
                  <span>🏆 Cérémonie de fin de formation · 45 min</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-yellow-500/25 border border-yellow-500/50 text-[9px]">
                    🔒 Enseignant · Élève
                  </span>
                </div>
                <div
                  className="text-lg sm:text-xl font-black leading-tight text-foreground"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Présentation immersive parents + élève
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  15 séquences officielles S5 · QCM Kahoot live · carte permis AM animée
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-yellow-300 flex-shrink-0" />
            </div>
          </Link>
        </section>

        {/* ============= 3 MODULES RÉGLEMENTAIRES ============= */}
        <section data-testid="am-modules">
          <div className="text-center mb-6">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-2">
              La formation en 3 modules
            </div>
            <h2
              className="text-2xl md:text-3xl font-black text-foreground tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              8 heures pour apprendre à conduire en sécurité
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <Link
                  key={m.id}
                  to={m.href}
                  reloadDocument
                  className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/15 via-red-500/8 to-transparent p-5 hover:border-red-500/60 hover:from-red-600/25 transition-all flex flex-col"
                  data-testid={`am-module-${m.id}`}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="pointer-events-none absolute -right-6 -bottom-4 opacity-10 group-hover:opacity-20 transition">
                    <Icon size={130} strokeWidth={1.2} style={{ color: m.accent }} />
                  </div>
                  <div className="relative flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className="text-5xl font-black leading-none text-red-500/25"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {m.num}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400"
                      >
                        <Clock className="w-3 h-3" /> {m.duree}
                      </span>
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-red-500 mb-2">
                      {m.kicker}
                    </div>
                    <h3
                      className="text-lg font-black leading-tight text-foreground mb-1.5"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {m.title}
                    </h3>
                    <p className="text-xs font-bold uppercase tracking-wide text-red-400 mb-3">{m.subtitle}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{m.desc}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-[10px] italic text-muted-foreground/70">{m.sessions}</span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
                        Découvrir <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ============= CONDITIONS D'ACCÈS ============= */}
        <section
          className="rounded-2xl border border-white/10 bg-black/20 p-5 md:p-6"
          data-testid="am-conditions"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-4 text-center">
            Conditions pour passer le permis AM
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CONDITIONS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-black/20"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-0.5">
                      {c.label}
                    </div>
                    <div className="text-sm text-foreground/90">{c.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============= MESSAGE FINAL ============= */}
        <section
          className="rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-600/15 via-red-500/8 to-transparent p-6 md:p-8 text-center"
          data-testid="am-final-message"
        >
          <div className="inline-flex items-center gap-1.5 mb-3">
            <Sparkles className="w-4 h-4 text-red-400" />
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400">
              Ce que tu vas apprendre
            </div>
          </div>
          <h3
            className="text-xl md:text-2xl font-black text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Le permis AM n&apos;est pas juste un « papier »
          </h3>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-6">
            C&apos;est ta première vraie formation à la conduite. Ce que tu vas apprendre ici te servira
            aussi si un jour tu passes le permis A2, A ou même B. Les bons réflexes se construisent
            dès la première leçon.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild variant="outline">
              <Link to="/ressources-pedagogiques">
                <BookOpen className="w-4 h-4 mr-1.5" /> Ressources pédagogiques
              </Link>
            </Button>
            <Button asChild className="bg-red-600 hover:bg-red-500 text-white font-black" data-testid="btn-read-livret-am-8h-online">
              <Link to="/livret-am/lecteur">
                <BookOpen className="w-4 h-4 mr-1.5" /> Lire en ligne (flipbook)
              </Link>
            </Button>
            <Button asChild variant="outline" className="font-black" data-testid="btn-download-livret-am-8h">
              <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/livrets/am-8h-2026/pdf?dl=1`} target="_blank" rel="noopener noreferrer">
                <Download className="w-4 h-4 mr-1.5" /> Télécharger le PDF
              </a>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
